import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import AdminShell from '../Layout/AdminShell';
import UserShell from '../Layout/UserShell';
import { PageHead, Panel, Pill, btn, field } from '../common/AdminUI';
import {
  ArrowLeft, Camera, User as UserIcon, AtSign, Mail, Phone, Globe, KeyRound,
  Users, Package, Wallet, Inbox, FileText, Hash, BarChart3, Plane, Ship, Send,
} from 'lucide-react';
import { uploadToS3, getCloudFrontUrl } from '../../utils/s3Upload';

// The profile belongs to whichever console you came from, so it keeps that
// console's shell around it rather than standing on a page of its own. The
// sidebar is there to get back; nothing here is one of its tabs, so nothing
// is marked current.
const CONSOLES = {
  super_admin: { path: '/super-admin', role: 'Super Admin', sections: [
    { title: 'Super admin core', items: [
      { id: 'users', label: 'Users Management', icon: Users },
      { id: 'shipments', label: 'All Shipments', icon: Package },
      { id: 'wallets', label: 'Wallet Management', icon: Wallet },
      { id: 'pendingApproval', label: 'Pending Approval', icon: Inbox },
      { id: 'clients', label: 'Client Master', icon: FileText },
      { id: 'hsn', label: 'HSC Codes', icon: Hash },
    ] },
  ] },
  admin: { path: '/admin', role: 'Admin', sections: [
    { title: 'Admin core', items: [
      { id: 'users', label: 'Users Management', icon: Users },
      { id: 'shipments', label: 'All Shipments', icon: Package },
      { id: 'pendingApproval', label: 'Pending Approval', icon: Inbox },
      { id: 'invoices', label: 'Client Master', icon: FileText },
      { id: 'hsn', label: 'HSC Codes', icon: Hash },
    ] },
    { title: 'Analytics', items: [{ id: 'insights', label: 'Sales Insights', icon: BarChart3 }] },
  ] },
  accountant: { path: '/accountant', role: 'Accountant', sections: [
    { title: 'Export', items: [
      { id: 'Air-Export', label: 'Air Export', icon: Plane },
      { id: 'Sea-Export', label: 'Sea Export', icon: Ship },
    ] },
    { title: 'Finance', items: [
      { id: 'Clients', label: 'Client Master', icon: Users },
      { id: 'HSN', label: 'HSC Codes', icon: Hash },
    ] },
    { title: 'Compliance', items: [{ id: 'KYC', label: 'KYC Documents', icon: FileText }] },
  ] },
  employee: { path: '/employee', role: 'Employee', sections: [
    { title: 'Export', items: [
      { id: 'Air-Export', label: 'Air Export', icon: Plane },
      { id: 'Sea-Export', label: 'Sea Export', icon: Ship },
    ] },
    { title: 'Approvals', items: [{ id: 'pendingApproval', label: 'Pending Approval', icon: Inbox }] },
  ] },
};

const ProfilePage = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showPassword, setShowPassword] = useState({ new: false, confirm: false });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBroke, setImageBroke] = useState(false);
  
  // Password change flow states
  const [passwordStep, setPasswordStep] = useState(1); // 1: Send OTP, 2: Verify OTP, 3: Set Password
  const [otp, setOtp] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    username: '',
    website: '',
    profilePicture: ''
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const normalizePhoneNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length > 10) {
      return digits.startsWith('91') ? digits.slice(-10) : digits.slice(-10);
    }
    return digits;
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Cleanup on unmount
    return () => {
      setFormData({
        name: '',
        email: '',
        mobile: '',
        username: '',
        website: '',
        profilePicture: ''
      });
      setImagePreview(null);
      setMessage({ type: '', text: '' });
      setPasswordMessage({ type: '', text: '' });
      setShowPasswordChange(false);
    };
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        mobile: normalizePhoneNumber(user.mobile || user.ph_num || ''),
        username: user.username || '',
        website: user.website || '',
        profilePicture: user.profilePicture || ''
      });
      const profilePicUrl = user.profilePicture 
        ? (user.profilePicture.startsWith('http') ? user.profilePicture : getCloudFrontUrl(user.profilePicture))
        : null;
      setImagePreview(profilePicUrl);
      setImageBroke(false);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordMessage({ type: '', text: '' });
  };

  // Resend timer effect
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Get formatted phone number
  const getFormattedPhone = () => {
    const mobile = formData.mobile || user?.mobile || user?.ph_num || '';
    const digits = String(mobile).replace(/\D/g, '');
    if (digits.length >= 10) {
      return `+91${digits.slice(-10)}`;
    }
    return '';
  };

  // Step 1: Send OTP for password change
  const handleSendOTP = async () => {
    const phoneNumber = getFormattedPhone();
    if (!phoneNumber) {
      setPasswordMessage({ type: 'error', text: 'Phone number not found' });
      return;
    }

    setOtpLoading(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const response = await apiCall('/users/password/change/send-otp', {
        method: 'POST',
        body: JSON.stringify({ phNum: phoneNumber })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordMessage({ type: 'success', text: 'OTP sent to your registered mobile number' });
        setPasswordStep(2);
        setResendTimer(60); // 60 seconds resend timer
      } else {
        setPasswordMessage({ type: 'error', text: data.message || 'Failed to send OTP' });
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setPasswordMessage({ type: 'error', text: 'An error occurred while sending OTP' });
    } finally {
      setOtpLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 4) {
      setPasswordMessage({ type: 'error', text: 'Please enter a valid OTP' });
      return;
    }

    const phoneNumber = getFormattedPhone();
    setOtpLoading(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const response = await apiCall('/users/password/change/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ phNum: phoneNumber, otp: otp })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setVerificationToken(data.verificationToken || data.data?.verificationToken);
        setPasswordMessage({ type: 'success', text: 'OTP verified! Now set your new password' });
        setPasswordStep(3);
        setOtp('');
      } else {
        setPasswordMessage({ type: 'error', text: data.message || 'Invalid OTP' });
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setPasswordMessage({ type: 'error', text: 'An error occurred while verifying OTP' });
    } finally {
      setOtpLoading(false);
    }
  };

  // Step 3: Change Password
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    if (!verificationToken) {
      setPasswordMessage({ type: 'error', text: 'Verification token expired. Please start again.' });
      resetPasswordFlow();
      return;
    }

    setLoading(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const response = await apiCall('/users/password/change', {
        method: 'POST',
        body: JSON.stringify({
          verificationToken: verificationToken,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully! Redirecting to login...' });
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        // Check if token expired
        if (data.message?.toLowerCase().includes('expired') || data.message?.toLowerCase().includes('invalid')) {
          setPasswordMessage({ type: 'error', text: 'Verification token expired. Please start again.' });
          resetPasswordFlow();
        } else {
          setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
        }
      }
    } catch (error) {
      setPasswordMessage({ type: 'error', text: 'An error occurred while changing password' });
      console.error('Change password error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset password flow
  const resetPasswordFlow = () => {
    setPasswordStep(1);
    setOtp('');
    setVerificationToken('');
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setResendTimer(0);
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setPasswordData({
      ...passwordData,
      newPassword: password,
      confirmPassword: password
    });
    setPasswordMessage({ type: 'success', text: 'Random password generated!' });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['jpg', 'jpeg', 'png', 'webp'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const fileMimeType = file.type.toLowerCase();
      
      const isValidType = allowedTypes.includes(fileExtension) || 
                          allowedTypes.some(type => fileMimeType.includes(type));
      
      if (!isValidType) {
        setMessage({ type: 'error', text: 'Only JPG, JPEG, PNG, and WEBP images are allowed' });
        e.target.value = ''; // Clear the input
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
        e.target.value = ''; // Clear the input
        return;
      }
      
      setProfileImage(file);
      setImageBroke(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let profilePicS3Key = formData.profilePicture;
      
      if (profileImage) {
        const extension = profileImage.name.split('.').pop();
        const fileName = `${user.id}.${extension}`;
        
        const presignResponse = await apiCall('/users/get-profile-pic-upload-url', {
          method: 'POST',
          body: JSON.stringify({
            userId: user.id,
            fileName: fileName
          })
        });
        
        if (!presignResponse.ok) {
          throw new Error('Failed to get pre-signed URL for profile picture');
        }
        
        const presignResult = await presignResponse.json();
        
        if (!presignResult.success || !presignResult.data) {
          throw new Error('Invalid response from server');
        }
        
        const uploadResponse = await fetch(presignResult.data.uploadUrl, {
          method: 'PUT',
          body: profileImage,
          headers: {
            'Content-Type': profileImage.type
          }
        });
        
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload profile picture to S3');
        }
        
        profilePicS3Key = presignResult.data.key;
      }
      
      const response = await apiCall(`${API_ENDPOINTS.UPDATE_USER}${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          Name: user?.name || formData.name,
          profilePic: profilePicS3Key
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        const updatedUser = {
          ...user,
          name: user?.name || formData.name,
          profilePicture: profilePicS3Key,
          profilePic: profilePicS3Key
        };
        updateUser(updatedUser);
        setProfileImage(null);
        setImagePreview(getCloudFrontUrl(profilePicS3Key));
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'An error occurred while updating profile' });
      console.error('Update profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const console_ = CONSOLES[user?.role];
  const goBack = () => navigate(console_ ? console_.path : '/user');

  const body = (
    <div className="max-w-4xl mx-auto">
      <PageHead
        eyebrow={`${console_?.role || 'Account'} · Profile`}
        title="Your Profile"
        badge={<Pill tone="blue">{user?.username || '—'}</Pill>}
        subtitle="Your sign-in details, and the picture that goes with them."
        actions={(
          <button type="button" onClick={goBack} className={btn.quiet}>
            <ArrowLeft size={16} /> Back to dashboard
          </button>
        )}
      />

      <Panel className="overflow-hidden mb-5">
        {/* who you are, with the picture you can change */}
        <div className="flex flex-col sm:flex-row items-center gap-5 px-5 sm:px-6 py-6
                        border-b border-slate-100 dark:border-slate-800
                        bg-gradient-to-r from-blue-50/70 to-transparent dark:from-blue-500/[0.07]">
          <div className="relative flex-shrink-0">
            <div className="w-24 h-24 rounded-2xl overflow-hidden ring-4 ring-white dark:ring-slate-800 shadow-lg">
              {imagePreview && (!imageBroke || imagePreview.startsWith('data:')) ? (
                <img
                  src={imagePreview}
                  alt="Profile"
                  onError={() => setImageBroke(true)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-[34px] font-black text-white
                                bg-gradient-to-br from-blue-500 to-blue-600">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
            </div>
            <label
              title="Change profile picture"
              className="absolute -bottom-1 -right-1 grid place-items-center w-9 h-9 rounded-xl cursor-pointer
                         bg-blue-600 text-white shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition"
            >
              <Camera size={16} />
              <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" onChange={handleImageChange} className="hidden" />
            </label>
          </div>

          <div className="flex-1 text-center sm:text-left min-w-0">
            <h2 className="text-[22px] font-black text-slate-900 dark:text-white truncate">{user?.name || 'User Profile'}</h2>
            <p className="text-[13px] font-mono font-semibold text-slate-500 dark:text-slate-400 truncate mt-0.5">
              {user?.username || 'USERNAME'}
            </p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1.5">
              {imageBroke
                ? 'Your picture is saved but could not be loaded — try uploading it again.'
                : 'Pick a picture, then save it below — the rest is set by your admin.'}
            </p>
          </div>
        </div>

        {message.text && (
          <div className={`mx-5 sm:mx-6 mt-5 px-4 py-3 rounded-xl text-[13px] font-semibold border ${
            message.type === 'success'
              ? 'border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
              : 'border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300'
          }`}>
            {message.text}
          </div>
        )}

        <div className="px-5 sm:px-6 py-5">
          <form onSubmit={handleUpdateProfile}>
            {/* Everything but the picture is set by an admin, so the fields
                read rather than invite. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: UserIcon, label: 'Full name', value: formData.name },
                { icon: AtSign, label: 'Username', value: formData.username, mono: true },
                { icon: Mail, label: 'Email address', value: formData.email },
                { icon: Phone, label: 'Mobile number', value: formData.mobile ? `+91 ${formData.mobile}` : '' },
                { icon: Globe, label: 'Website', value: formData.website, span: true },
              ].map((f) => (
                <div key={f.label} className={f.span ? 'sm:col-span-2' : ''}>
                  <label className="flex items-center gap-1.5 mb-1.5 text-[11px] font-black uppercase tracking-[0.1em]
                                    text-slate-400 dark:text-slate-500">
                    <f.icon size={13} /> {f.label}
                  </label>
                  <div className={`h-11 flex items-center px-3.5 rounded-xl text-[13.5px] font-semibold
                                   border border-slate-200 bg-slate-50 text-slate-700
                                   dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-200
                                   ${f.mono ? 'font-mono' : ''}`}>
                    <span className="truncate">{f.value || '—'}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="submit"
                disabled={loading || !profileImage}
                className={`${btn.primary} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Saving…' : 'Save profile picture'}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordChange(!showPasswordChange);
                  if (!showPasswordChange) {
                    resetPasswordFlow();
                    setPasswordMessage({ type: '', text: '' });
                  }
                }}
                className="flex items-center justify-between w-full text-left group"
              >
                <span className="flex items-center gap-2.5">
                  <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                                   bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
                    <KeyRound size={16} />
                  </span>
                  <span>
                    <span className="block text-[14px] font-bold text-slate-900 dark:text-white">Change password</span>
                    <span className="block text-[12px] text-slate-500 dark:text-slate-400">
                      We send a code to your email first.
                    </span>
                  </span>
                </span>
                <span className={`grid place-items-center w-8 h-8 rounded-lg transition
                                  text-slate-400 group-hover:bg-slate-100 dark:group-hover:bg-slate-800
                                  ${showPasswordChange ? 'rotate-180' : ''}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>

              {showPasswordChange && (
                <div className="mt-6 space-y-4">
                  {passwordMessage.text && (
                    <div className={`px-4 py-3 rounded-xl text-[13px] font-semibold border ${
                      passwordMessage.type === 'success' 
                        ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                        : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
                    }`}>
                      {passwordMessage.text}
                    </div>
                  )}

                  {/* Step Indicator */}
                  <div className="flex items-center justify-center space-x-2 mb-6">
                    {[1, 2, 3].map((step) => (
                      <div key={step} className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          passwordStep >= step 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                        }`}>
                          {passwordStep > step ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : step}
                        </div>
                        {step < 3 && (
                          <div className={`w-12 h-1 mx-1 ${
                            passwordStep > step ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'
                          }`} />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center text-xs text-slate-500 dark:text-slate-400 mb-4">
                    <span className={`mx-2 ${passwordStep === 1 ? 'text-blue-600 font-medium' : ''}`}>Send OTP</span>
                    <span className={`mx-2 ${passwordStep === 2 ? 'text-blue-600 font-medium' : ''}`}>Verify</span>
                    <span className={`mx-2 ${passwordStep === 3 ? 'text-blue-600 font-medium' : ''}`}>Set Password</span>
                  </div>

                  {/* Step 1: Send OTP */}
                  {passwordStep === 1 && (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4
                                      dark:border-slate-800 dark:bg-slate-900/50">
                        <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                          OTP will be sent to your registered mobile number
                        </p>
                        <p className="text-lg font-medium text-slate-900 dark:text-white">
                          {getFormattedPhone() ? `${getFormattedPhone().slice(0, 4)}****${getFormattedPhone().slice(-4)}` : 'Phone not found'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleSendOTP}
                        disabled={otpLoading || !getFormattedPhone()}
                        className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpLoading ? (
                          <>
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Sending OTP...</span>
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            <span>Send OTP</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Step 2: Verify OTP */}
                  {passwordStep === 2 && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Enter OTP <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={otp}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(value);
                            setPasswordMessage({ type: '', text: '' });
                          }}
                          placeholder="Enter 6-digit OTP"
                          maxLength={6}
                          className="h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition h-14 text-center text-[22px] font-black tracking-[0.35em] font-mono"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => {
                            setPasswordStep(1);
                            setOtp('');
                            setPasswordMessage({ type: '', text: '' });
                          }}
                          className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                        >
                          ← Back
                        </button>
                        <button
                          type="button"
                          onClick={handleSendOTP}
                          disabled={resendTimer > 0 || otpLoading}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={handleVerifyOTP}
                        disabled={otpLoading || otp.length < 4}
                        className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpLoading ? (
                          <>
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Verifying...</span>
                          </>
                        ) : 'Verify OTP'}
                      </button>
                    </div>
                  )}

                  {/* Step 3: Set New Password */}
                  {passwordStep === 3 && (
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-[13px] font-semibold text-amber-700
                                      dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Token valid for 5 minutes. Complete the password change quickly.</span>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={generateRandomPassword}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Generate Random Password
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          New Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword.new ? "text" : "password"}
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            required
                            placeholder="Enter your new password"
                            className="h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition pr-11"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                          >
                            {showPassword.new ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Confirm New Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword.confirm ? "text" : "password"}
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            required
                            placeholder="Re-enter your new password"
                            className="h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition pr-11"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                          >
                            {showPassword.confirm ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Password match indicator */}
                      {passwordData.newPassword && passwordData.confirmPassword && (
                        <div className={`text-sm flex items-center gap-2 ${
                          passwordData.newPassword === passwordData.confirmPassword 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {passwordData.newPassword === passwordData.confirmPassword ? (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span>Passwords match</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span>Passwords do not match</span>
                            </>
                          )}
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={resetPasswordFlow}
                          className="flex-1 inline-flex items-center justify-center h-11 px-4 rounded-xl text-[13px] font-bold transition
                                     border border-slate-200 bg-white text-slate-600 hover:bg-slate-50
                                     dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
                        >
                          Start Over
                        </button>
                        <button
                          type="submit"
                          disabled={loading || passwordData.newPassword !== passwordData.confirmPassword}
                          className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl text-[13px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex-1"
                        >
                          {loading ? 'Updating...' : 'Update Password'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
          </div>
        </div>
      </Panel>
    </div>
  );

  // The user dashboard and the consoles wear different shells; the profile
  // wears whichever one you came from.
  return console_ ? (
    <AdminShell
      sections={console_.sections}
      activeId={null}
      onSelect={() => navigate(console_.path)}
      role={console_.role}
    >
      {body}
    </AdminShell>
  ) : (
    <UserShell activeTab={null} onTabChange={() => navigate('/user')}>
      {body}
    </UserShell>
  );
};

export default ProfilePage;
